import { createFormat } from '@morpho-org/morpho-ts'

import type { BigIntLike } from '../types/index'

import { parseBigInt } from './bigint'

/**
 * Stands in for a value there is no figure for: not applicable, failed, or not computable. Shared so
 * a screen can't mix glyphs for the same meaning. A value still being read gets a loading
 * placeholder instead, since a dash claims nothing is coming.
 */
export const EMPTY_VALUE_PLACEHOLDER = '—'

type FormatterDefaults = NonNullable<NonNullable<Parameters<typeof createFormat>[0]>['all']>

/** Largest value the short formatter compacts; above it, it renders `> 999.99T`. */
export const MAX_SHORT_FORMAT_VALUE = 999.99e12

/** Builds the shared en-US formatter set, with app-specific defaults layered on top. */
export const createFormatters = (defaults: FormatterDefaults = {}) =>
  createFormat({ all: { locale: 'en-US', ...defaults }, short: { max: MAX_SHORT_FORMAT_VALUE } })

export const formatters = createFormatters()

/** morpho-ts' `.max(null)` keeps an inherited cap, so opting out needs a formatter built without one. */
export const uncappedShortFormatter = createFormat({ all: { locale: 'en-US' } }).short

export const formatUint256 = formatters.commas.readable().digits(2).min(0).createOf()

export const formatUint256Percent = (value: bigint, digits = 2, tailingZeros = false) =>
  formatters.number.digits(digits).trailingZero(tailingZeros).unit('%').of(value, 16)

type FormatTokenBalanceOptions = {
  digits?: number | null
  min?: number
  /** Overrides the short format's `> 999.99T` cap; `null` removes it. */
  max?: number | null
  format?: 'short' | 'full'
}

type FormatTokenBalanceParams = {
  value: bigint | null | undefined
  decimals: number | null | undefined
  symbol: string | null | undefined
  options?: FormatTokenBalanceOptions | number | null
}

export const formatTokenBalance = ({
  value,
  decimals,
  symbol,
  options
}: FormatTokenBalanceParams) => {
  const opts: FormatTokenBalanceOptions =
    typeof options === 'number' || options == null ? { digits: options ?? undefined } : options

  const { digits = 2, min, max, format = 'short' } = opts
  const base =
    format === 'full' ? formatters.commas : max === null ? uncappedShortFormatter : formatters.short

  let f = base
    .digits(digits)
    .trailingZero(false)
    .unit(symbol ?? '')
    .default('0')

  if (min != null) {
    f = f.min(min)
  } else if (format === 'short') {
    f = f.min(0.01)
  }

  if (max != null) f = f.max(max)

  return f.of(value ?? 0n, decimals ?? 18)
}

type FormatTokenBalanceFullParams = {
  value: bigint | null | undefined
  decimals: number | null | undefined
  symbol: string | null | undefined
  digits?: number | null
  min?: number
}

export const formatTokenBalanceFull = ({
  value,
  decimals,
  symbol,
  digits,
  // Clamp tiny residuals to a "< 0.01" dust readout by default, matching the short
  // format. Pass `min: 0` to opt out and show full precision (e.g. high-digit displays).
  min = 0.01
}: FormatTokenBalanceFullParams) =>
  formatTokenBalance({
    value,
    decimals,
    symbol,
    options: { digits: digits ?? undefined, format: 'full', min }
  })

export function formatUsdValue(value: number | string | null | undefined) {
  return formatters.short.digits(2).trailingZero(false).unit('$').default('0').of(Number(value))
}

/** USD value that renders the empty placeholder instead of `$0` when the source is missing. */
export function formatUsdValueOrPlaceholder(value: number | string | null | undefined) {
  return value == null || value === '' ? EMPTY_VALUE_PLACEHOLDER : formatUsdValue(value)
}

/**
 * Formats a market's on-chain oracle price into a human-readable ratio.
 *
 * The Morpho API normalizes `market.state.price` so the human value is
 * `price / 10^(36 - collateralDecimals + loanDecimals)` — the price of 1 unit of
 * collateral expressed in loan assets. Returns `undefined` when the price or
 * either decimals value is missing, so callers can apply their own fallback.
 */
type FormatOraclePriceParams = {
  value: BigIntLike | null | undefined
  collateralDecimals: number | null | undefined
  loanDecimals: number | null | undefined
  loanSymbol?: string | null
}

export function formatOraclePrice({
  value,
  collateralDecimals,
  loanDecimals,
  loanSymbol
}: FormatOraclePriceParams) {
  const price = parseBigInt(value)
  if (price == null || collateralDecimals == null || loanDecimals == null) return undefined

  // Clamp to [0, 100] to avoid an Intl.NumberFormat RangeError on exotic decimals.
  const priceDecimals = Math.max(0, Math.min(100, 36 - collateralDecimals + loanDecimals))

  return formatters.commas
    .digits(getOraclePriceDigits(price, priceDecimals))
    .min(1e-8)
    .trailingZero(false)
    .unit(loanSymbol ?? '')
    .of(price, priceDecimals)
}

/** Show extra precision for sub-1 ratios so they don't collapse to "0.00". */
function getOraclePriceDigits(price: bigint, decimals: number) {
  const totalDigits = price.toString().length
  if (totalDigits - decimals >= 1) return 2
  const leadingZeros = decimals - totalDigits
  return Math.min(leadingZeros + 3, 20)
}

export function formatRate(value: bigint | number | undefined) {
  if (value === undefined) return undefined
  // If it's a number, it's already a decimal (e.g., 0.05 for 5%)
  // If it's a bigint, it's in WAD format (1e18)
  if (typeof value === 'number') {
    return `${(value * 100).toFixed(2)}%`
  }
  return formatters.percent.digits(2).unit('%').of(value, 18)
}
